<template>
  <LoadingIndicator
    absolute
    v-if="!teiData"
  />
  <TEIDataError
    v-else-if="errorMessage"
    :message="errorMessage"
  />
  <TEILayoutContainer
    v-else
    :asset-id="assetId"
    :show-check-button="showCheckButton"
    :show-checking-button="submittingAttempt"
    :show-reset-button="showCheckButton"
    :show-try-again-button="showTryAgainButton"
    :check-button-enabled="checkButtonEnabled"
    :reset-button-enabled="resetButtonEnabled"
    :scoring-bar="{
      attempts: (showFeedback || updatingAttemptItemTry) ? tries : tries + 1,
      maxAttempts: maxTries,
      score: maxScore,
      showAnswerKey: showAnswerKey,
    }"
    :show-scoring-bar="!showFeedback"
    :show-response-bar="showFeedback"
    :response-bar="{
      state: attemptItemTry.correct ? 'correct' : 'incorrect',
      content: responseBarMessage,
    }"
    :is-displayed="isDisplayed"
    :style="{
      '--dragged-term-x': draggedTermX,
      '--dragged-term-y': draggedTermY,
    }"
    @check-button-clicked="onCheckClicked"
    @reset-button-clicked="onResetClicked"
    @try-again-button-clicked="onTryAgainClicked"
  >
    <div
      class="tiling-tei"
      :class="`tiling-tei--${termStyle}`"
    >
      <TEIInstructionsCard
        v-if="teiData.prompt.lang[teiLang]"
        position="left"
        class="tiling-tei__instructions-card"
        :content="promptContent"
        do-sticker-position="top-right"
        :image-alt="promptImageAlt"
        :image-url="promptImageUrl"
        :max-lines="promptMaxLines"
      />
      <div class="tiling-tei__flex-spacer" />
      <div class="tiling-tei__response-wrapper">
        <div class="tiling-tei__terms-wrapper">
          <Draggable
            :disabled="!interactive"
            drag-class="tei-tiling-term--dragging"
            :force-fallback="true"
            ghost-class="tei-tiling-term--ghost"
            :group="{
              name: 'tiling-tei',
              pull: 'clone',
              put: false,
              revertClone: true,
            }"
            item-key="id"
            :sort="false"
            :list="terms"
            tag="ul"
            class="tiling-tei__terms"
            :class="`tiling-tei__terms--${termStyle}`"
            :style="{
              '--column-count': Math.ceil(terms.length / 2) // keep grid to 2 rows
            }"
            @start="onDragStart"
            @end="onDragEnd"
          >
            <template #item="{ element: term }">
              <TEITilingTerm
                :disabled="!interactive"
                :format="termStyle"
                :layout="teiData.uiLayout.type"
                :term="term"
                :data-term-id="term.id"
                @select-keyboard-term="selectKeyboardTerm(term)"
              />
            </template>
          </Draggable>
        </div>
        <div
          v-if="teiData.uiLayout.type === 'tableWithColumn'"
          class="tiling-tei__response-columns"
        >
          <div
            v-for="column in columns"
            :key="column.id"
            class="tiling-tei__response-column-wrapper"
          >
            <div
              class="tiling-tei__response-column-header"
              :aria-label="$t('%(columnName)s column', {
                columnName: extractText(column.text)
              })"
              tabindex="0"
            >
              <span aria-hidden="true">{{ extractText(column.text) }}</span>
              <!-- This is the hidden text that is read by screen readers using NVDA -->
              <span class="nebula-screenreader-only">
                {{ $t('%(columnName)s column', {
                  columnName: extractText(column.text)
                }) }}
              </span>
            </div>
            <div
              class="tiling-tei__response-column-body"
              :aria-label="ariaAnswerBox"
              tabindex="0"
            >
              <Draggable
                tag="ul"
                class="tiling-tei__response-column"
                :class="[
                  `tiling-tei__response-column--${termStyle}`,
                  {
                    'tiling-tei__response-column--2-col-images': termStyle === 'images'
                      && columns.length === 2
                  }
                ]"
                :disabled="!interactive"
                drag-class="tei-tiling-term--dragging"
                :force-fallback="true"
                ghost-class="tei-tiling-term--ghost"
                :group="{
                  name: 'tiling-tei',
                  put: (...args) => canAddToColumn(column, ...args),
                }"
                item-key="id"
                :sort="false"
                :list="column.responses"
                @change="onChange(column.id, $event)"
                @start="onDragStart"
                @end="onDragEnd"
              >
                <template #header>
                  <div class="tiling-tei__drop-icon-matte">
                    <DropIcon class="tiling-tei__drop-icon" />
                  </div>
                  <TEITilingTerm
                    v-if="column.includesFloatingTerm
                      && !column.responses.some(
                        (response) => response.id === selectedKeyboardTerm.id,
                      ) && !(termStyle === 'text' && column.responses.length >= 4)"
                    class="tei-tiling-term--ghost"
                    :format="termStyle"
                    :layout="teiData.uiLayout.type"
                    :term="selectedKeyboardTerm"
                  />
                </template>
                <template #item="{ element: term }">
                  <TEITilingTerm
                    class="tiling-tei__response-item"
                    :disabled="!interactive"
                    :format="termStyle"
                    :layout="teiData.uiLayout.type"
                    :term="term"
                    :data-term-id="term.id"
                    is-in-column
                    :state="term.state"
                    @remove-term="removeTerm(column.id, term.id)"
                    @arrow-key="onSelectedKeyboardTermKeydown(e = null, term, column)"
                  />
                </template>
              </Draggable>
            </div>
          </div>
        </div>
        <div
          v-else-if="teiData.uiLayout.type === 'flow'"
          v-expandTilingFlowContent="{
            componentId: uniqueId,
            columns,
            interactive,
            onChange,
            onDragEnd,
            onDragStart,
            removeTerm,
            onSelectedKeyboardTermKeydown,
          }"
          class="tiling-tei__flow-content"
        >
          <!-- There is an internal wrapper here to make sure the
            flex parent doesn't affect the layout inside -->
          <div v-html="flowContent" />
        </div>
      </div>
      <div class="tiling-tei__flex-spacer" />
    </div>
    <div v-if="selectedKeyboardTerm">
      <FocusTrap
        active
        click-outside-deactivates
        initial-focus="li"
        fallback-focus="li"
        :tabbable-options="{ includeContainer: true }"
        @deactivate="clearSelectedKeyboardTerm"
      >
        <TEITilingTerm
          floating
          :format="termStyle"
          :layout="teiData.uiLayout.type"
          :term="selectedKeyboardTerm"
          :data-term-id="selectedKeyboardTerm.id"
          :style="selectedKeyboardTermStyle"
          @keydown="onSelectedKeyboardTermKeydown"
        />
      </FocusTrap>
    </div>
    <div
      aria-live="polite"
      class="nebula-screenreader-only"
    >
      {{ ariaLiveMessage }}
    </div>
  </TEILayoutContainer>
</template>

<script>
import { FocusTrap } from 'focus-trap-vue';
import {
  isNumber,
  uniqueId,
  without,
  cloneDeep,
  set,
} from 'lodash-es';
import Draggable from 'vuedraggable';
import { mapActions, mapState } from 'pinia';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import TEILayoutContainer from '@/components/teis/TEILayoutContainer.vue';
import TEIDataError from '@/components/teis/TEIDataError.vue';
import TEIInstructionsCard from '@/components/teis/TEIInstructionsCard.vue';
import TEITilingTerm from '@/components/teis/TEITilingTerm.vue';
import DropIcon from '@/components/ui-elements/texas-icons/DropIcon.vue';
import expandTilingFlowContent from '@/directives/expandTilingFlowContent';
import {
  TEI_CHECKING_ANSWERS_MODAL_DELAY,
  TEI_INSTRUCTIONS_CARD_ALLOWED_ELEMENTS,
} from '@/lib/constants';
import { extractHtml, extractTextFromHtml } from '@/lib/utils';
import fullSlideTEI from '@/mixins/fullSlideTEI';
import modals from '@/mixins/modals';
import {
  useAssessmentStore,
  useUserStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'TilingTEI',
  components: {
    Draggable,
    DropIcon,
    FocusTrap,
    LoadingIndicator,
    TEILayoutContainer,
    TEIDataError,
    TEIInstructionsCard,
    TEITilingTerm,
  },
  mixins: [fullSlideTEI, modals],
  directives: {
    expandTilingFlowContent,
  },
  data() {
    return {
      ariaLiveMessage: null,
      checkingAnswersModalId: 'TEICheckingAnswersModal',
      draggedItemMouseOffsetX: 0,
      draggedItemMouseOffsetY: 0,
      draggedTermX: 0,
      draggedTermY: 0,
      response: {},
      selectedKeyboardTerm: null,
      selectedKeyboardTermColumnIndex: null,
      selectedKeyboardTermStyle: null,
      transformOriginDimensions: {},
      uniqueId: uniqueId(),
      updatingAttemptItemTry: false,
      previousIndex: null,
    };
  },
  computed: {
    ...mapState(useUserStore, [
      'userIsStudent',
    ]),
    ...mapState(useAssessmentStore, [
      'attemptIsClearing',
    ]),
    answerLayoutError() {
      if (!['tableWithColumn', 'flow'].includes(this.teiData.uiLayout.type)) {
        return this.$t('The answer layout "%(type)s" is not supported for Tiling TEIs.', {
          type: this.teiData.uiLayout.type,
        });
      }

      return false;
    },
    checkButtonEnabled() {
      const responseHasItems = Object.values(this.response)
        .some((val) => val?.objects?.length);

      return !!(responseHasItems
        && this.userCanEditAttempt
        && !this.submittingAttempt
        && !this.attemptItemTry.correct);
    },
    columns() {
      return this.teiData.responseFields.find((rf) => rf.type === 'label')
        .elements.map((column, idx) => {
          // If we're showing the answer key, show only the correct answers.
          // Otherwise show the user response from the attempt
          const responseIds = this.showAnswerKey
            ? this.correctAnswers[column.id].objects
            : (this.response[column.id]?.objects || []);

          return {
            id: column.id,
            includesFloatingTerm: this.selectedKeyboardTermColumnIndex === idx,
            maxAccepted: column.maxAccepted,
            responses: responseIds
              .map((termId) => this.getFormattedTermById(termId, column.id)),
            text: extractHtml(column.lang[this.teiLang].text),
          };
        });
    },
    correctAnswers() {
      const correctScoringOption = this.teiData.scoringData.scoring
        .find((scoringOption) => scoringOption.correct);

      if (correctScoringOption?.responses?.length) {
        return correctScoringOption.responses[0];
      }

      // Students don't have access to the answer key, so create a
      // blank answer key.
      return this.teiData.responseFields.find((rf) => rf.type === 'label')
        .elements.reduce((acc, curr) => {
          acc[curr.id] = { objects: [] };
          return acc;
        }, {});
    },
    errorMessage() {
      return this.teiData.error
        || this.termsCountError
        || this.fieldsCountError
        || this.answerLayoutError
        || this.flowContentTableError;
    },
    fieldsCountError() {
      if (this.teiData.uiLayout.type === 'flow' && this.columns.length > 4) {
        return this.$t('Tiling TEIs with more than 4 fields cannot be used in this view.');
      }

      if (this.teiData.uiLayout.type === 'tableWithColumn' && this.columns.length > 3) {
        return this.$t('Tiling TEIs with more than 3 columns cannot be used in this view.');
      }

      return null;
    },
    flowContent() {
      const content = this.teiData.responseFields.find((rf) => rf.type === 'content')
        .elements[0].lang[this.teiLang].text;

      return extractHtml(
        content,
        new Set(['br', 'div', 'span']),
        true,
        new Set(['tei-answerfield-holder']),
      );
    },
    flowContentTableError() {
      const content = this.teiData.responseFields.find((rf) => rf.type === 'content');
      if (!content) return false;

      const contentText = content.elements[0].lang[this.teiLang].text;

      if (contentText.includes('<table')) {
        return this.$t('Using HTML tables in Tiling TEI flow content is not supported in this view.');
      }

      return false;
    },
    hasTriesRemaining() {
      return !this.userIsStudent || !isNumber(this.tries)
        || this.tries < this.maxTries;
    },
    interactive() {
      return !this.submittingAttempt && !this.showFeedback
        && !this.showAnswerKey && this.userCanEditAttempt && !this.attemptItemTry.correct;
    },
    maxTries() {
      return this.attempt?.max_tries || 0;
    },
    promptContent() {
      const prompt = this.teiData?.prompt?.lang?.[this.teiLang]?.text;
      return extractHtml(prompt, new Set(TEI_INSTRUCTIONS_CARD_ALLOWED_ELEMENTS));
    },
    promptImageAlt() {
      return this.teiData.prompt.lang[this.teiLang]?.resource?.[0]?.title;
    },
    promptImageUrl() {
      return this.teiData.prompt.lang[this.teiLang]?.resource?.[0]?.url;
    },
    promptMaxLines() {
      if (this.promptImageUrl) {
        // Calculate how much space the image is taking up, including padding,
        // and subtract that from the total space (13 lines)
        const promptImageDimensions = this.teiData.prompt.lang[this.teiLang]
          ?.resource?.[0]?.dimensions;

        // Default to 8 since that's the amount of lines left if we had
        // the largest possible image
        if (!promptImageDimensions?.height) return 8;

        const promptImageHeight = Math.min(
          (promptImageDimensions.height / promptImageDimensions.width) * 200,
          130, // the image is capped at 130px in css
        );
        const lineHeight = 31;
        const imageHeightInLines = Math.ceil((promptImageHeight + 16) / lineHeight);

        return 13 - imageHeightInLines;
      }

      return 13;
    },
    responseBarMessage() {
      // check multilang feedback first
      if (
        this.attemptItemTry?.multilanguage_feedback
        && this.teiLang
        && this.attemptItemTry?.multilanguage_feedback[this.teiLang]
      ) {
        return this.attemptItemTry?.multilanguage_feedback[this.teiLang];
      }

      if (this.attemptItemTry?.try_feedback) {
        return this.attemptItemTry?.try_feedback;
      }

      return this.hasTriesRemaining
        ? this.$t('Don’t worry! You will have another chance at this question.')
        : this.$t('Hm...Not quite. Check with your teacher if you have questions.');
    },
    resetButtonEnabled() {
      const responseHasItems = Object.values(this.response)
        .some((val) => val?.objects?.length);

      return !!(responseHasItems
        && this.userCanEditAttempt
        && !this.submittingAttempt
        && !this.attemptItemTry.correct);
    },
    showAnswerKey() {
      return this.mode === 'EDIT';
    },
    showCheckButton() {
      return !this.showFeedback && !this.submittingAttempt;
    },
    showTryAgainButton() {
      return this.showFeedback && !this.attemptItemTry.correct
        && this.hasTriesRemaining && !this.submittingAttempt;
    },
    terms() {
      return this.teiData.responseFields.find((rf) => rf.type === 'object')
        .elements.map(this.formatTerm);
    },
    termsCountError() {
      if (this.terms.length > 6) {
        return this.$t('TEIs with more than 6 term options cannot be used in this view.');
      }

      return null;
    },
    termStyle() {
      return this.terms[0].imageUrl ? 'images' : 'text';
    },
    ariaAnswerBox() {
      const termType = this.termStyle;
      if (termType === 'images') {
        return this.$t('image answer box');
      }
      return this.$t('%(termType)s answer box', { termType });
    },
  },
  async created() {
    this.response = this.attemptItemTry.response || {};

    // If we've already submitted once before, show the feedback immediately
    this.showFeedback = this.attemptItemTry.checked;
  },
  beforeUnmount() {
    window.removeEventListener('mousemove', this.updateDraggedTermPosition);
    window.removeEventListener('touchmove', this.updateDraggedTermPosition);
  },
  methods: {
    ...mapActions(useAssessmentStore, [
      types.SUBMIT_RESPONSE,
      types.UPDATE_UNSUBMITTED_ATTEMPT,
      types.SET_ATTEMPT_CLEARING,
    ]),
    canAddToColumn(column, to, from, el) {
      // The dropped term is only passed as its DOM element so we need to read
      // its id from a DOM attribute
      const termId = el.getAttribute('data-term-id');

      if (column.responses.some((response) => response.id === termId)) {
        // Do not allow duplicate terms to be added to a column
        return false;
      }

      if (this.termStyle === 'text' && column.responses.length >= 4) {
        // For text terms, do not allow more than 4 in a column
        return false;
      }

      return true;
    },
    formatTerm(term) {
      return {
        accessibilityContent: term.lang[this.teiLang].accessibility
          || term.lang[this.teiLang].resource?.mediaCaption,
        id: term.id,
        imageAlt: term.lang[this.teiLang].resource?.title,
        imageUrl: term.lang[this.teiLang].resource?.url,
        maxClone: term.maxClone,
        text: term.lang[this.teiLang].text,
      };
    },
    getFormattedTermById(termId, columnId) {
      const term = this.teiData.responseFields.find((rf) => rf.type === 'object')
        .elements.find((element) => element.id === termId);
      let state = null;
      const correctAnswerForColumn = this.correctAnswers[columnId]?.objects || [];

      if (
        // Correct if: showing answer key
        (this.showAnswerKey && correctAnswerForColumn.includes(termId))
        // OR the attempt is checked and this option is correct
        || (this.showFeedback && this.attemptItemTry.checked
          && this.attemptItemTry.correct_answers[0]?.[columnId]?.includes(termId))
      ) {
        state = 'correct';
      } else if (this.showFeedback && this.attemptItemTry.checked
          && !this.attemptItemTry.correct_answers[0]?.[columnId]?.includes(termId)) {
        state = 'incorrect';
      }

      return {
        ...this.formatTerm(term),
        state,
      };
    },
    onChange(columnId, e) {
      if (e.added) {
        if (this.response[columnId]?.objects) {
          this.response[columnId].objects.splice(e.added.newIndex, 0, e.added.element.id);
        } else {
          set(this.response, columnId, {
            objects: [e.added.element.id],
          });
        }
      }

      if (e.moved) {
        this.response[columnId].objects.splice(e.moved.oldIndex, 1);
        this.response[columnId].objects.splice(e.moved.newIndex, 0, e.moved.element.id);
      }

      if (e.removed) {
        this.response[columnId].objects.splice(e.removed.oldIndex, 1);
      }

      // When the user's answer changes, if there are responses
      // left, flag the attempt as dirty so they get prompted
      // before navigating to another page (and losing their
      // unsubmitted attempt)
      const responseHasItems = Object.values(this.response)
        .some((val) => val?.objects?.length);
      this[types.UPDATE_UNSUBMITTED_ATTEMPT](responseHasItems);
    },
    onCheckClicked() {
      this.checkAnswersModal = this.openModalDeferred(TEI_CHECKING_ANSWERS_MODAL_DELAY, {
        id: this.checkingAnswersModalId,
        type: this.checkingAnswersModalId,
      });
      this.submittingAttempt = true;
      this[types.SUBMIT_RESPONSE]({
        teiId: this.assetId,
        payload: this.response,
      });
    },
    onDragStart(e) {
      // We have to position the floating "ghost" item since the library doesn't
      // work well when things have been scaled, like in the Studio canvas
      const draggedElDimensions = e.item
        .querySelector('.tei-tiling-term__image-wrapper, .tei-tiling-term__inner-wrap')
        .getBoundingClientRect();
      const itemIdx = [...e.item.parentNode.children].indexOf(e.item);
      const transformOriginDimensions = e.item.closest('.full-slide-asset')
        .getBoundingClientRect();

      // Keep track of offsets so that when the ghost item appears, it's in the same
      // place as when the user clicked it, rather than to the bottom-right of the
      // cursor
      const offsetX = e.originalEvent.x - draggedElDimensions.x;
      this.draggedItemMouseOffsetX = (offsetX + transformOriginDimensions.x);

      const offsetY = e.originalEvent.y - draggedElDimensions.y;
      this.draggedItemMouseOffsetY = (offsetY + transformOriginDimensions.y);

      // Add additional offsets for items dragged from response columns, since
      // the original dragged item changes dimensions after a drag begins
      if (e.from.matches('.tiling-tei__response-column--2-col-images')) {
        if (itemIdx % 3 === 0) {
          this.draggedItemMouseOffsetX -= (210 * this.scale);
        } else if (itemIdx % 3 === 2) {
          this.draggedItemMouseOffsetX -= (105 * this.scale);
        }
        this.draggedItemMouseOffsetY -= (72 * this.scale * Math.floor((itemIdx - 1) / 3));
      } else if (e.from.matches('.tiling-tei__response-column--images')) {
        if (itemIdx % 2 === 0) {
          this.draggedItemMouseOffsetX -= (105 * this.scale);
        }
        this.draggedItemMouseOffsetY -= (72 * this.scale * Math.floor((itemIdx - 1) / 2));
      } else if (e.from.matches('.tiling-tei__response-column--text')) {
        this.draggedItemMouseOffsetY -= (72 * this.scale * (itemIdx - 1));
      }

      this.draggedTermX = `${(e.originalEvent.x - this.draggedItemMouseOffsetX) / this.scale}px`;
      this.draggedTermY = `${(e.originalEvent.y - this.draggedItemMouseOffsetY) / this.scale}px`;

      // Begin listening to mouse movements so the ghost item can be repositioned
      // as the mouse moves about
      window.addEventListener('mousemove', this.updateDraggedTermPosition);
      window.addEventListener('touchmove', this.updateDraggedTermPosition);
    },
    onDragEnd() {
      window.removeEventListener('mousemove', this.updateDraggedTermPosition);
      window.removeEventListener('touchmove', this.updateDraggedTermPosition);
    },
    onSelectedKeyboardTermKeydown(e, term, column) {
      // If a user is moving the floating term with the keyboard,
      // they need to reset values and remove the existing term
      if (this.selectedKeyboardTerm === null) {
        this.selectedKeyboardTerm = term;
        this.removeTerm(column.id, term.id);
      }

      // If the user is moving the term with the keyboard,
      // reset the column index if value type is null
      if (typeof this.selectedKeyboardTermColumnIndex === 'number') {
        this.previousIndex = cloneDeep(this.selectedKeyboardTermColumnIndex);
      } else {
        this.selectedKeyboardTermColumnIndex = this.previousIndex;
      }

      if (e) {
        e.preventDefault();
      }

      const key = e?.key;

      if (['Left', 'ArrowLeft', 'Up', 'ArrowUp'].includes(key)) {
        // Move floating term to the previous response column
        if (this.selectedKeyboardTermColumnIndex === 0) {
          this.selectedKeyboardTermColumnIndex = this.columns.length - 1;
        } else {
          this.selectedKeyboardTermColumnIndex -= 1;
        }
      } else if (['Right', 'ArrowRight', 'Down', 'ArrowDown'].includes(key)) {
        // Move floating term to the next response column
        if (this.selectedKeyboardTermColumnIndex === this.columns.length - 1) {
          this.selectedKeyboardTermColumnIndex = 0;
        } else {
          this.selectedKeyboardTermColumnIndex += 1;
        }
      } else if (key === ' ') {
        // The spacebar places the term in the current column
        const selectedColumn = this.columns[this.selectedKeyboardTermColumnIndex];
        const columnObjects = this.response[selectedColumn.id]?.objects || [];

        if (this.teiData.uiLayout.type === 'flow' && columnObjects.length) {
          this.ariaLiveMessage = this.$t('This blank is full.');
        } else if (this.termStyle === 'text' && columnObjects.length >= 4) {
          this.ariaLiveMessage = this.$t('This column is full.');
        } else if (columnObjects.includes(this.selectedKeyboardTerm.id)) {
          this.ariaLiveMessage = this.$t('This column already contains this term.');
        } else {
          if (columnObjects.length === 0) {
            set(this.response, selectedColumn.id, {
              objects: [this.selectedKeyboardTerm.id],
            });
          } else {
            columnObjects.push(this.selectedKeyboardTerm.id);
          }

          this.ariaLiveMessage = this.$t('Term placed.');
          this.clearSelectedKeyboardTerm();
        }
      }
    },
    clearSelectedKeyboardTerm() {
      this.selectedKeyboardTerm = null;
      this.selectedKeyboardTermColumnIndex = null;
    },
    onResetClicked() {
      this.response = {};
    },
    onTryAgainClicked() {
      this.response = {};
      this.showFeedback = false;
    },
    selectKeyboardTerm(term) {
      this.selectedKeyboardTerm = term;
      this.selectedKeyboardTermColumnIndex = 0;
    },
    extractText(html) {
      return extractTextFromHtml(html);
    },
    removeTerm(columnId, termId) {
      this.response[columnId].objects = without(this.response[columnId].objects, termId);
      const responseHasItems = Object.values(this.response)
        .some((val) => val?.objects?.length);

      this[types.UPDATE_UNSUBMITTED_ATTEMPT](responseHasItems);
    },
    updateDraggedTermPosition(e) {
      let { x, y } = e;

      if (e.touches) {
        x = e.touches[0].clientX;
        y = e.touches[0].clientY;
      }
      this.draggedTermX = `${(x - this.draggedItemMouseOffsetX) / this.scale}px`;
      this.draggedTermY = `${(y - this.draggedItemMouseOffsetY) / this.scale}px`;
    },
  },
  watch: {
    attemptItemTry(newTry) {
      // if we've just cleared answers
      if (this.attemptIsClearing && !newTry.checked && this.isDisplayed) {
        this.submittingAttempt = false;
        this.response = newTry.response || {};
        this.showFeedback = !!newTry.checked;

        // set attempt cleared
        this[types.SET_ATTEMPT_CLEARING](false);
      }

      // This watcher is triggered when a response comes back from the api
      if (this.checkAnswersModal) {
        // Make sure the try number doesn't flicker between renders
        this.updatingAttemptItemTry = true;

        this.checkAnswersModal.close(() => {
          this[types.UPDATE_UNSUBMITTED_ATTEMPT](false);
          this.submittingAttempt = false;
          this.response = newTry.response || {};
          this.showFeedback = newTry.checked;
          this.updatingAttemptItemTry = false;
        });
      }
    },
    selectedKeyboardTermColumnIndex(idx) {
      const $columns = this.$el.querySelectorAll('.tei-tiling-flow-field, .tiling-tei__response-column');
      if (!$columns[idx]) return;

      const { x, y, width } = $columns[idx].getBoundingClientRect();

      // getBoundingClientRect measures from the viewport, but the floating term
      // is positioned relative to the editor canvas
      const { x: canvasX, y: canvasY } = this.$el.getBoundingClientRect();

      // The coordinates inside the canvas are scaled, but the viewport coordinates
      // from getBoundingClientRect are not.
      let translateX = (x - canvasX) / this.scale;
      let translateY = (y - canvasY) / this.scale;
      let floatingTermWidth;

      if (this.teiData.uiLayout.type === 'tableWithColumn') {
        // Columns include 8px padding, which should not be factored in
        translateX += 8;
        translateY += 8;

        if (this.termStyle === 'text') {
          floatingTermWidth = width / this.scale - 16; // account for 16px padding in columns
        }
      } else if (this.teiData.uiLayout.type === 'flow') {
        // Match width of in-flow answer fields
        floatingTermWidth = 204;
      }

      // The floating term needs to show slightly offset from the column (10px) so
      // it's clear that it hasn't been placed yet
      this.selectedKeyboardTermStyle = {
        transform: `translate(${translateX + 10}px, ${translateY - 10}px)`,
        width: floatingTermWidth ? `${floatingTermWidth}px` : null,
      };
      if (this.teiData.uiLayout.type === 'tableWithColumn') {
        this.ariaLiveMessage = this.$t('Column %(colName)s', {
          colName: extractTextFromHtml(this.columns[idx].text),
        });
      } else if (this.teiData.uiLayout.type === 'flow') {
        this.ariaLiveMessage = this.$t('Blank %(num)d', { num: idx + 1 });
      }
    },
  },
};
</script>

<style lang="stylus">
@import './styles/tei-fonts';
$column-header-height = $nebula-space-5x;
$inset-border-radius = $nebula-border-radius-large - 3px;

.tiling-tei {
  color: $nebula-color-platform-neutral-900;
  display: flex;
  font-family: 'Mikado Chan', 'Proxima Vara', Arial, sans-serif;
  font-feature-settings: 'ss02' 0;
  height: 100%;

  &__instructions-card {
    flex-shrink: 0;
    height: auto;
    margin: 20px 0;
  }

  &__flex-spacer {
    flex-grow: 0;
    flex-shrink: 0;
    width: $nebula-space-3x;
  }

  &__response-wrapper {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    flex-shrink: 1;
    min-width: 0;
    padding-bottom: 41px; // clear buttons with 10px padding
  }

  &__terms-wrapper {
    align-items: center;
    display: flex;
    flex-grow: 0;
    justify-content: center;
    min-height: 130px;
  }

  &__terms {
    display: flex;
    justify-content: center;
    padding: 0;

    &--text {
      column-gap: $nebula-space-3x;
      display: grid;
      grid-template-columns: repeat(var(--column-count), 1fr);
      margin: 10px;
      row-gap: 6px;
    }

    &--images {
      gap: 6px;
      margin: 35px 0;
    }
  }

  &__response-columns {
    column-gap: 13px;
    display: flex;
    flex-grow: 1;
    flex-wrap: nowrap;
    margin: 0;
    max-width: 100%;
    padding: 0;
  }

  &__response-column-wrapper {
    border: 3px solid $nebula-color-white;
    border-radius: $nebula-border-radius-large;
    flex-basis: 100%; // keep all columns the same width
    flex-shrink: 1;
    list-style-type: none;
    margin: 0;
    min-width: 0;
    padding: 0;
  }

  &__response-column-header {
    tei-mikado-body-secondary-bold();

    align-items: center;
    background-color: var(--tei-button-primary-color);
    border-radius: $inset-border-radius $inset-border-radius 0 0;
    color: $nebula-color-white;
    display: flex;
    height: $column-header-height;
    justify-content: center;
    padding: 0 $nebula-space-2x;
    white-space: nowrap;

    * {
      // Prevent wrapping in nested html for column headers
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__response-column-body {
    height: "calc(100% - %s)" % $column-header-height;
    position: relative;

    &:focus {
      border-radius: 0 0 21px 21px;
    }
  }

  &__response-column {
    align-content: flex-start;
    background-color: var(--tei-button-primary-disabled-color);
    border-radius: 0 0 $inset-border-radius $inset-border-radius;
    display: grid;
    gap: 6px;
    height: 100%;
    list-style-type: none;
    margin: 0;
    padding: $nebula-space-1x;
    position: relative;

    &--images {
      grid-template-columns: 1fr 1fr;
    }

    &--2-col-images {
      grid-template-columns: 99px 99px 99px;
    }

    &--text {
      grid-template-columns: 1fr;
    }

    .tei-tiling-term--ghost {
      bottom: $nebula-space-1x;
      height: auto;
      left: $nebula-space-1x;
      position: absolute;
      right: $nebula-space-1x;
      top: $nebula-space-1x;

      .tei-tiling-term__inner-wrap,
      .tei-tiling-term__check-icon-wrapper {
        // Using opacity to hide these instead of display: none, so
        // that .tei-tiling-term__inner-wrap can still be used in
        // the drag height calculations
        opacity: 0;
      }

      .tei-tiling-term__image-wrapper,
      .tei-tiling-term__inner-wrap {
        box-shadow: none;
      }

      .tei-tiling-term__ghost {
        display: flex;
      }
    }
  }

  &__drop-icon-matte {
    align-items: center;
    background-color: var(--tei-column-matte-color);
    border-radius: $nebula-border-radius-default;
    bottom: $nebula-space-1x;
    display: none;
    justify-content: center;
    left: $nebula-space-1x;
    position: absolute;
    right: $nebula-space-1x;
    top: $nebula-space-1x;

    // The drop matte is always present when using image terms, but hides for text
    // terms once a term has been added
    &:only-child,
    .tiling-tei--images & {
      display: flex;
    }
  }

  &__drop-icon {
    display: none;
    fill: $nebula-color-white;
    height: $nebula-space-3x;
    width: $nebula-space-3x;

    // For image terms, even if the matte is displaying, the icon should hide after a term has
    // been added
    .tiling-tei__drop-icon-matte:only-child & {
      display: block;
    }
  }

  &__flow-content {
    align-items: center;
    background-color: $nebula-color-white;
    border-radius: $nebula-border-radius-large;
    display: flex;
    justify-content: flex-start;
    line-height: 3.3; // allow vertical space between flow fields
    margin: auto 0; // center vertically between terms and "Check" button
    min-height: 227px;
    padding: $nebula-space-1x $nebula-space-2x;
  }
}
</style>
